import {
  CHEVRON_WING_FACTOR,
  DOT_RADIUS_FACTOR,
} from '~/client/src/shared/components/MapMarkups/markupArrowhead'
import {
  HIGHLIGHTER_OPACITY,
  NO_FILL,
} from '~/client/src/shared/components/MapMarkups/MarkupDrawController'
import {
  HANDLE_ACCENT,
  HANDLE_FILL,
  VERTEX_RADIUS,
  VERTEX_STROKE_WIDTH,
} from '~/client/src/shared/components/MapMarkups/markupHandleStyle'
import type { ArrowheadType } from '~/client/src/shared/components/MapMarkups/markupTools'

import {
  IViewport,
  layoutText,
  MeasureText,
  outlinePoints,
} from './photoMarkupGeometry'
import {
  IPathMarkup,
  IPoint,
  IRect,
  isClosedPath,
  ISize,
  isTextMarkup,
  ITextMarkup,
  JPEG_QUALITY,
  PhotoMarkup,
} from './photoMarkupModel'

export interface ISelectionChrome {
  box: IRect
  handles: IPoint[]
}

export interface IScene {
  view: IViewport
  imageSize: ISize
  unit: number
  markups: PhotoMarkup[]
  preview: IPathMarkup | null
  vertices: IPoint[]
  chrome: ISelectionChrome | null
}

const CHEVRON_HALF_ANGLE = (33 * Math.PI) / 180
const CHEVRON_LENGTH_CAP = 0.25
const DOT_LENGTH_CAP = 0.15
const UNDERLINE_OFFSET = 0.55
const UNDERLINE_THICKNESS = 0.07

function contextMeasure(context: CanvasRenderingContext2D): MeasureText {
  return (text, font) => {
    context.font = font
    return context.measureText(text).width
  }
}

function tracePath(
  context: CanvasRenderingContext2D,
  points: IPoint[],
  isClosed: boolean,
): void {
  context.beginPath()
  points.forEach((p, i) =>
    i ? context.lineTo(p.x, p.y) : context.moveTo(p.x, p.y),
  )
  if (isClosed) context.closePath()
}

function drawCap(
  context: CanvasRenderingContext2D,
  type: ArrowheadType,
  previous: IPoint,
  tip: IPoint,
  width: number,
): void {
  const length = Math.hypot(tip.x - previous.x, tip.y - previous.y)
  if (type === 'none' || !length) return
  if (type === 'circle') {
    const radius = Math.min(width * DOT_RADIUS_FACTOR, length * DOT_LENGTH_CAP)
    context.beginPath()
    context.arc(tip.x, tip.y, radius, 0, Math.PI * 2)
    context.fill()
    return
  }
  const wing = Math.min(
    width * CHEVRON_WING_FACTOR,
    length * CHEVRON_LENGTH_CAP,
  )
  const angle = Math.atan2(previous.y - tip.y, previous.x - tip.x)
  const wingPoint = (offset: number): IPoint => ({
    x: tip.x + wing * Math.cos(angle + offset),
    y: tip.y + wing * Math.sin(angle + offset),
  })
  context.setLineDash([])
  context.lineCap = 'round'
  tracePath(
    context,
    [wingPoint(CHEVRON_HALF_ANGLE), tip, wingPoint(-CHEVRON_HALF_ANGLE)],
    false,
  )
  context.stroke()
}

function drawPath(
  context: CanvasRenderingContext2D,
  markup: IPathMarkup,
  unit: number,
): void {
  const points = outlinePoints(markup)
  if (points.length < 2) return
  const width = markup.strokeWidth * unit
  const isClosed = isClosedPath(markup)
  context.save()
  context.globalAlpha = markup.kind === 'highlighter' ? HIGHLIGHTER_OPACITY : 1
  context.lineJoin = 'round'
  if (isClosed && markup.fillColor !== NO_FILL) {
    context.fillStyle = markup.fillColor
    tracePath(context, points, true)
    context.fill()
  }
  const isDashed = markup.strokeStyle === 'dashed'
  context.strokeStyle = markup.color
  context.fillStyle = markup.color
  context.lineWidth = width
  context.lineCap = isDashed ? 'butt' : 'round'
  context.setLineDash(isDashed ? [width * 2, width * 2] : [])
  tracePath(context, points, isClosed)
  context.stroke()
  if (markup.kind === 'arrow') {
    const last = points.length - 1
    drawCap(context, markup.arrowheadStart, points[1], points[0], width)
    drawCap(context, markup.arrowheadEnd, points[last - 1], points[last], width)
  }
  context.restore()
}

function drawText(
  context: CanvasRenderingContext2D,
  markup: ITextMarkup,
  unit: number,
): void {
  const layout = layoutText(markup, unit, contextMeasure(context))
  context.save()
  if (markup.fillColor !== NO_FILL) {
    context.fillStyle = markup.fillColor
    context.fillRect(markup.at.x, markup.at.y, layout.width, layout.height)
  }
  context.font = layout.font
  context.fillStyle = markup.color
  context.textBaseline = 'middle'
  const left = markup.at.x + layout.paddingPx
  layout.lines.forEach((line, i) => {
    const middle =
      markup.at.y +
      layout.paddingPx +
      i * layout.lineHeightPx +
      layout.lineHeightPx / 2
    context.fillText(line, left, middle)
    if (markup.underline && line) {
      const thickness = Math.max(1, layout.fontPx * UNDERLINE_THICKNESS)
      context.fillRect(
        left,
        middle + layout.fontPx * UNDERLINE_OFFSET - thickness,
        context.measureText(line).width,
        thickness,
      )
    }
  })
  context.restore()
}

function drawMarkup(
  context: CanvasRenderingContext2D,
  markup: PhotoMarkup,
  unit: number,
): void {
  if (isTextMarkup(markup)) {
    drawText(context, markup, unit)
  } else {
    drawPath(context, markup, unit)
  }
}

function drawHandle(context: CanvasRenderingContext2D, point: IPoint): void {
  context.beginPath()
  context.arc(point.x, point.y, VERTEX_RADIUS, 0, Math.PI * 2)
  context.fillStyle = HANDLE_FILL
  context.fill()
  context.lineWidth = VERTEX_STROKE_WIDTH
  context.strokeStyle = HANDLE_ACCENT
  context.stroke()
}

function drawSelectionChrome(
  context: CanvasRenderingContext2D,
  chrome: ISelectionChrome,
): void {
  const { box, handles } = chrome
  context.save()
  context.setLineDash([])
  context.lineWidth = 1
  context.strokeStyle = HANDLE_ACCENT
  context.strokeRect(
    Math.round(box.minX) + 0.5,
    Math.round(box.minY) + 0.5,
    Math.round(box.maxX - box.minX),
    Math.round(box.maxY - box.minY),
  )
  handles.forEach(handle => drawHandle(context, handle))
  context.restore()
}

function resizeCanvas(
  canvas: HTMLCanvasElement,
  size: ISize,
  pixelRatio: number,
): void {
  const width = Math.round(size.width * pixelRatio)
  const height = Math.round(size.height * pixelRatio)
  if (canvas.width !== width) canvas.width = width
  if (canvas.height !== height) canvas.height = height
}

function applyView(
  context: CanvasRenderingContext2D,
  view: IViewport,
  pixelRatio: number,
): void {
  const scale = view.scale * pixelRatio
  context.setTransform(
    scale,
    0,
    0,
    scale,
    view.offsetX * pixelRatio,
    view.offsetY * pixelRatio,
  )
}

export function drawImageLayer(
  canvas: HTMLCanvasElement,
  stageSize: ISize,
  pixelRatio: number,
  image: CanvasImageSource | null,
  imageSize: ISize | null,
  view: IViewport | null,
): void {
  resizeCanvas(canvas, stageSize, pixelRatio)
  const context = canvas.getContext('2d')
  context.setTransform(1, 0, 0, 1, 0, 0)
  context.clearRect(0, 0, canvas.width, canvas.height)
  if (!image || !imageSize || !view) return
  applyView(context, view, pixelRatio)
  context.imageSmoothingQuality = 'high'
  context.drawImage(image, 0, 0, imageSize.width, imageSize.height)
}

export function drawScene(
  canvas: HTMLCanvasElement,
  stageSize: ISize,
  pixelRatio: number,
  scene: IScene | null,
): void {
  resizeCanvas(canvas, stageSize, pixelRatio)
  const context = canvas.getContext('2d')
  context.setTransform(1, 0, 0, 1, 0, 0)
  context.clearRect(0, 0, canvas.width, canvas.height)
  if (!scene) return
  applyView(context, scene.view, pixelRatio)
  context.save()
  context.beginPath()
  context.rect(0, 0, scene.imageSize.width, scene.imageSize.height)
  context.clip()
  scene.markups.forEach(markup => drawMarkup(context, markup, scene.unit))
  if (scene.preview) drawMarkup(context, scene.preview, scene.unit)
  context.restore()
  context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
  scene.vertices.forEach(point => drawHandle(context, point))
  if (scene.chrome) drawSelectionChrome(context, scene.chrome)
}

function createCanvas({ width, height }: ISize): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

function exportJpeg(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  )
}

function toJpegName(name: string): string {
  return `${(name || 'photo').replace(/\.[^.]+$/, '')}.jpg`
}

export async function renderPhoto(
  image: CanvasImageSource,
  imageSize: ISize,
  markups: PhotoMarkup[],
  unit: number,
  fileName: string,
  create: (size: ISize) => HTMLCanvasElement = createCanvas,
): Promise<File> {
  const canvas = create(imageSize)
  try {
    const context = canvas.getContext('2d')
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, imageSize.width, imageSize.height)
    context.drawImage(image, 0, 0, imageSize.width, imageSize.height)
    markups.forEach(markup => drawMarkup(context, markup, unit))
    const blob = await exportJpeg(canvas)
    if (!blob) throw new Error('Photo markup export failed')
    return new File([blob], toJpegName(fileName), { type: 'image/jpeg' })
  } finally {
    canvas.width = canvas.height = 0
  }
}
